import { ReactElement, ReactNode } from 'react';
import { useIntl } from 'react-intl';

import CardGrid from '@/components/app/cards/CardGrid';
import { Pack, ProgramOrGuideItem } from '@/components/app/cards/PacksList/types';
import { useAnalytics } from '@/hooks/analytics';
import { useComponentDidMount } from '@/hooks/utils/useComponentDidMount';

import messages from '../messages';
import { OnResultClick } from '../types';
import { GridSearchResultsStyled, NoResults, RegularText } from './styles';

interface Props {
	pack?: Pack;
	packItems?: ProgramOrGuideItem[];
	query: string;
	category: string[];
	duration: string[];
	query_id?: string;
	search_index?: string;
	usedFallback: boolean;
	onResultClick: OnResultClick;
}
interface SearchProps {
	query_id?: string;
	search_index?: string;
	filter_category: string[];
	filter_duration: string[];
	query: string;
	location: 'app' | 'meeting experience';
}

const SearchResults: React.FC<Props> = ({
	pack,
	packItems,
	query,
	category,
	duration,
	query_id,
	search_index,
	usedFallback,
}) => {
	const { formatMessage } = useIntl();
	const { logEvent } = useAnalytics();
	const location = 'app';
	const searchQueryProps: SearchProps = {
		query_id,
		search_index,
		filter_category: category,
		filter_duration: duration,
		query,
		location,
	};

	useComponentDidMount(() => {
		if (packItems && packItems.length > 0) {
			logEvent({
				eventName: 'Search : Results : Shown',
				eventProps: {
					filter_category: category,
					filter_duration: duration,
					query,
					query_character_count: query.length,
					total_search_results_returned: packItems.length,
					location,
				},
			});
		}
	});

	if (!query && category.length === 0 && duration.length === 0) {
		return null;
	}

	const formattedFilters = [...category, ...duration]
		.map((filter, index) => <b key={index}>{filter}</b>)
		.reduce((acc: ReactNode[], curr, index) => {
			if (index === 0) {
				return [curr];
			} else if (index === category.length - 1) {
				return [...acc, ' and ', curr];
			} else {
				return [...acc, ', ', curr];
			}
		}, []);

	if (!pack || !packItems?.length) {
		return (
			<NoResults>
				<RegularText>
					{formatMessage(messages.noResults, {
						query: query || '',
						formattedFilters: <>{formattedFilters}</>,
						b: (...chunks: ReactNode[]): ReactElement => <b>{chunks}</b>,
					})}
				</RegularText>
				<RegularText>{formatMessage(messages.searchSuggestion)}</RegularText>
			</NoResults>
		);
	}

	return (
		<>
			{usedFallback && (
				<NoResults>
					{formatMessage(messages.fallBackResults, {
						query,
						formattedFilters: <>{formattedFilters}</>,
						b: (...chunks: ReactNode[]): ReactElement => <b>{chunks}</b>,
					})}
				</NoResults>
			)}

			<GridSearchResultsStyled>
				<CardGrid pack={pack} searchQuery={{ ...searchQueryProps }} />
			</GridSearchResultsStyled>
		</>
	);
};

export default SearchResults;
